import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOpportunitiesStore } from "~/app/sales/opportunities/store";

import type { OrgStages } from "~/lib/org-stages/types";
import useUserSession from "~/lib/organizations/hooks/use-user-session";

import { axios, getBackendApiErrorResponse } from "../axios";
import { REACT_QUERY_KEY } from "../constants/common";
import type { BackendApiResponse } from "../types/backend-api-response.type";

export const useOrgStagesData = ({
  enabled,
  pipeline_id,
  refetchOnMount,
}: {
  enabled?: boolean;
  pipeline_id?: string | null;
  refetchOnMount?: boolean | "always";
}) => {
  const { userSession } = useUserSession();
  const setStages = useOpportunitiesStore(state => state.setStages);

  const {
    isLoading: loadingStages,
    data: stages,
    refetch: refetchStagesData,
  } = useQuery({
    queryKey: [REACT_QUERY_KEY.ORG_STAGES, pipeline_id],
    queryFn: async () => {
      try {
        const { data } = await axios.get<BackendApiResponse<OrgStages[]>>(
          `/stage?pipeline_id=${pipeline_id}`,
        );
        if (data.data) setStages(data.data);
        return data.data;
      } catch (error) {
        const err = getBackendApiErrorResponse(error);
        throw new Error(err?.data.message ?? "Something went wrong!");
      }
    },
    enabled:
      enabled === undefined
        ? !!userSession && !!pipeline_id
        : !!userSession && enabled && !!pipeline_id,
    retry: 1,
    refetchOnMount: refetchOnMount ?? "always",
    refetchOnWindowFocus: refetchOnMount ?? "always",
  });

  return { loadingStages, stages, refetchStagesData };
};

export const useInvalidateOrgStagesData = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEY.ORG_STAGES] });
  };
};
